import { useTranslation } from "react-i18next";
import { LuRadio } from "react-icons/lu";
import { match, useColorScheme } from "@opencast/appkit";

import {
    ThumbnailImg,
    ThumbnailItemState,
    ThumbnailOverlay,
    ThumbnailOverlayContainer,
    ThumbnailReplacement,
} from "./Thumbnail";
import { COLORS } from "../color";
import { AccessRoles } from "../util";


type ThumbnailStackProps = {
    title: string;
    thumbnails?: readonly ThumbnailInfo[];
    kind: "series" | "playlist";
    className?: string;
    accessRoles?: AccessRoles;
}

export const ThumbnailStack: React.FC<ThumbnailStackProps> = ({
    thumbnails,
    title,
    kind,
    className,
    accessRoles,
}) => {
    const { isDark } = useColorScheme();

    return (
        <div {...{ className }} css={{
            position: "relative",
            zIndex: 0,
            margin: "0 auto",
            width: "70%",
            display: "grid",
            gridAutoColumns: "1fr",
            "> div": {
                position: "relative",
                borderRadius: 8,
                // The outline needs to be in a pseudo element as otherwise, it is
                // hidden behind the img for some reason.
                "::after": {
                    content: "''",
                    position: "absolute",
                    inset: 0,
                    borderRadius: 8,
                    outline: `2px solid ${COLORS.neutral70}`,
                    outlineOffset: -2,
                },
            },
            "> div:not(:last-child)": {
                boxShadow: "3px -2px 6px rgba(0, 0, 0, 40%)",
            },
            "> div:nth-child(1)": {
                zIndex: 3,
                gridColumn: match(kind, {
                    series: () => "1 / span 10",
                    playlist: () => "2 / span 10",
                }),
                gridRow: "3 / span 10",
            },
            "> div:nth-child(2)": {
                zIndex: 2,
                gridColumn: match(kind, {
                    series: () => "2 / span 10",
                    playlist: () => "1 / span 9",
                }),
                gridRow: match(kind, {
                    series: () => "2 / span 10",
                    playlist: () => "2 / span 9",
                }),
            },
            "> div:nth-child(3)": {
                zIndex: 1,
                gridColumn: match(kind, {
                    series: () => "3 / span 10",
                    playlist: () => "3 / span 8",
                }),
                gridRow: match(kind, {
                    series: () => "1 / span 10",
                    playlist: () => "1 / span 8",
                }),
            },
        }}>
            {thumbnails && thumbnails.slice(0, 3).map((info, idx) => <div key={idx}>
                <SeriesThumbnail
                    accessRoles={accessRoles && idx === 0 ? accessRoles : undefined}
                    {...{ info, title }}
                />
            </div>)}
            {/* Add fake thumbnails to always have 3. The visual image of 3 things behind each other
                is more important than actually showing the correct number of thumbnails. */}
            {[...Array(Math.max(0, 3 - (thumbnails?.length ?? 0)))].map((_, idx) => (
                <div key={"dummy" + idx}>
                    <DummySeriesStackThumbnail
                        accessRoles={accessRoles && idx === 0 ? accessRoles : undefined}
                        isDarkScheme={isDark}
                    />
                </div>
            ))}
        </div>
    );
};


type DummySeriesStackThumbnailProps = {
    isDarkScheme: boolean;
    accessRoles?: AccessRoles;
}
const DummySeriesStackThumbnail: React.FC<DummySeriesStackThumbnailProps> = ({
    isDarkScheme,
    accessRoles,
}) => <ThumbnailOverlayContainer {...{ accessRoles }} css={{
    // Pattern from https://css-pattern.com/overlapping-cubes/,
    // MIT licensed: https://github.com/Afif13/CSS-Pattern
    "--s": "40px",
    ...isDarkScheme ? {
        "--c1": "#2c2c2c",
        "--c2": "#292929",
        "--c3": "#262626",
    } : {
        "--c1": "#e8e8e8",
        "--c2": "#e3e3e3",
        "--c3": "#dddddd",
    },

    "--_g": "0 120deg,#0000 0",
    background: `
        conic-gradient(             at calc(250%/3) calc(100%/3),
            var(--c3) var(--_g)),
        conic-gradient(from -120deg at calc( 50%/3) calc(100%/3),
            var(--c2) var(--_g)),
        conic-gradient(from  120deg at calc(100%/3) calc(250%/3),
            var(--c1) var(--_g)),
        conic-gradient(from  120deg at calc(200%/3) calc(250%/3),
            var(--c1) var(--_g)),
        conic-gradient(from -180deg at calc(100%/3) 50%,
            var(--c2)  60deg,var(--c1) var(--_g)),
        conic-gradient(from   60deg at calc(200%/3) 50%,
            var(--c1)  60deg,var(--c3) var(--_g)),
        conic-gradient(from  -60deg at 50% calc(100%/3),
            var(--c1) 120deg,var(--c2) 0 240deg,var(--c3) 0)
    `,
    backgroundSize: "calc(var(--s)*sqrt(3)) var(--s)",
}} />;

type ThumbnailInfo = {
    readonly audioOnly: boolean;
    readonly live: boolean;
    readonly url: string | null | undefined;
    readonly state: ThumbnailItemState;
}

type SeriesThumbnailProps = {
    info: ThumbnailInfo;
    title: string;
    accessRoles?: AccessRoles;
}

const SeriesThumbnail: React.FC<SeriesThumbnailProps> = ({ info, title, accessRoles }) => {
    const { t } = useTranslation();
    const { isDark } = useColorScheme();

    let inner;
    if (info.url != null) {
        // We have a proper thumbnail.
        inner = <ThumbnailImg
            src={info.url}
            alt={t("series.entry-of-series-thumbnail", { series: title })}
        />;
    } else {
        inner = <ThumbnailReplacement
            audioOnly={info.audioOnly}
            videoState={info.state}
            {...{ isDark }}
        />;
    }

    const overlay = <ThumbnailOverlay backgroundColor="rgba(200, 0, 0, 0.9)">
        <LuRadio css={{ fontSize: 19, strokeWidth: 1.4 }} />
        {t("video.live")}
    </ThumbnailOverlay>;

    return <ThumbnailOverlayContainer accessRoles={accessRoles}>
        {inner}
        {info.live && overlay}
    </ThumbnailOverlayContainer>;
};
